import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Check } from "lucide-react";

import { useFeedDefinitions, type FeedDefinitionOut } from "../api";

/** The delivery-model registry (jrae 2026-08-20): HOW each feed arrives.
 *  Seeded 'suggested' from the code scan (singer taps, backendsafe pollers)
 *  + measured arrival cadence. The table is a read-only index — click a row
 *  to open the definition drawer, where everything (cadence, grains, notes)
 *  is edited and the changelog lives (jrae 2026-08-20 post-launch review).
 *  Declared cadence drives how fast 'silent' fires; credential/data grain
 *  routes the level-outage sweeps. */
export function FeedDefinitionsPanel({
  onOpen,
}: {
  onOpen: (d: FeedDefinitionOut) => void;
}) {
  const defs = useFeedDefinitions();

  const columns: DataTableColumn<FeedDefinitionOut>[] = [
    {
      key: "entity_source",
      header: "Integration",
      render: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">{r.entity_source}</span>
          <span className="text-xs text-muted-foreground">{r.delivery_mode}</span>
        </div>
      ),
    },
    { key: "cadence", header: "Cadence" },
    {
      key: "expected_interval_h",
      header: "Expect every",
      numeric: true,
      render: (r) =>
        r.expected_interval_h != null ? `${r.expected_interval_h}h` : "learned",
    },
    { key: "credential_grain", header: "Credentials at" },
    {
      key: "monitor_l2_outages",
      header: "Watch companies",
      render: (r) =>
        r.monitor_l2_outages ? <Check className="h-4 w-4 text-success" /> : null,
    },
    {
      key: "monitor_l3_outages",
      header: "Watch locations",
      render: (r) =>
        r.monitor_l3_outages ? <Check className="h-4 w-4 text-success" /> : null,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <StatusBadge
          value={r.status === "confirmed" ? "approved" : "pending"}
          label={r.status}
        />
      ),
    },
    {
      key: "notes",
      header: "Notes",
      render: (r) => (
        <span className="block max-w-72 truncate text-xs text-muted-foreground">
          {r.notes ?? "—"}
        </span>
      ),
    },
  ];

  return (
    <div>
      <h2 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Feed definitions — how each integration delivers
      </h2>
      <p className="mb-2 text-xs text-muted-foreground">
        Suggested from the integration code + measured arrival cadence. Click a row to
        open the record — edit cadence, grains, and notes there, with the full change
        log of how our understanding evolved. Declared cadence decides how fast
        &quot;silent&quot; fires; the grains decide where the company/location outage
        sweeps look.
        {/* Phase 2 (COPS-3 §8c): a mappings explorer lives here — every
            integration's source-field → Cinch-field mapping, mined from the
            singer-tap + backendsafe code, opening into the standard drawers
            with the feed definition on the quick view. Deferred by jrae
            2026-08-20; do not build ahead of the card. */}
        {" "}Phase 2 adds a mappings explorer: what each source system sends and where
        it lands in Cinch, mined from the integration code.
      </p>
      <DataTable
        columns={columns}
        rows={defs.data ?? []}
        rowKey={(r) => r.id}
        loading={defs.isLoading}
        onRowClick={onOpen}
        emptyMessage="Definitions seed on the first nightly run."
      />
    </div>
  );
}
